import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Icon } from "../Icon";
import { ATTACHMENT, COMPANY } from "../data";
import { useSeo } from "../ui";
import {
  FORM_INTRO,
  LANDING_PROCESS,
  PRODUCT_SEGMENTS,
  LEAD_BUDGETS,
  LEAD_GOV,
  LEAD_OPTIONS,
  LEAD_QTYS,
  LEAD_STAGES,
  LEAD_VIDEO,
  REPLY_DAYS,
  SEGMENT_BY_SLUG,
} from "./content";
import { getAttribution, lastSegment, trackVisit } from "./tracking";
import { WEBSITE_FORM, WEBSITE_SLUG } from "./website";

/* =========================================================
   공통 문의 페이지 — /for/contact?from=<slug>
   모든 랜딩의 [문의하기] 가 여기로 모인다. 분야(from)와 유입 채널(utm)은
   자동으로 함께 보낸다. 필드 이름은 백엔드 app/landing.py 의 Form 과 일치해야 한다.
   from=website(가게 홈페이지)이면 같은 API 로 보내되 문구 · 항목을 홈페이지용으로 바꾼다.
   ========================================================= */
const EMPTY = {
  name: "",
  phone: "",
  email: "",
  idea: "",
  stage: "",
  desired_option: "",
  budget: "",
  due: "",
  quantity: "",
  gov_support: "",
  video_ok: "",
  website: "", // 봇 트랩
};

function formatSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  return kb < 1024 ? `${Math.round(kb)} KB` : `${(kb / 1024).toFixed(1)} MB`;
}

function Select({ id, label, value, onChange, options, disabled }) {
  return (
    <div className="form__field">
      <label className="form__label" htmlFor={id}>{label}</label>
      <select id={id} className="form__input form__select" value={value} onChange={onChange} disabled={disabled}>
        <option value="">선택 안 함</option>
        {options.map((o) => {
          const [v, l] = Array.isArray(o) ? o : [o, o];
          return <option key={v} value={v}>{l}</option>;
        })}
      </select>
    </div>
  );
}

/* 홈페이지 문의 — 희망 오픈 시점 · 참고 자료만 받는다 (나머지 선택 항목은 제품 개발용) */
function WebExtras({ form, update, sending, file, fileRef, onPickFile, clearFile }) {
  return (
    <div className="form__row">
      <div className="form__field">
        <label className="form__label" htmlFor="lf-due">{WEBSITE_FORM.dueLabel} <span className="form__opt">(선택)</span></label>
        <input id="lf-due" className="form__input" type="text" maxLength={120} placeholder={WEBSITE_FORM.duePlaceholder}
          value={form.due} onChange={update("due")} disabled={sending} />
      </div>
      <div className="form__field">
        <label className="form__label" htmlFor="lf-file">참고 자료 <span className="form__opt">(선택)</span></label>
        <input ref={fileRef} id="lf-file" className="form__file" type="file"
          accept={ATTACHMENT.accept} onChange={onPickFile} disabled={sending} aria-describedby="lf-file-hint" />
        {file ? (
          <p className="form__file-picked">
            {file.name} <span>{formatSize(file.size)}</span>
            <button type="button" className="form__file-clear" onClick={clearFile} disabled={sending}>제거</button>
          </p>
        ) : (
          <p id="lf-file-hint" className="form__hint">{WEBSITE_FORM.fileHint}, {ATTACHMENT.hint}</p>
        )}
      </div>
    </div>
  );
}

function LeadForm({ initialSegment }) {
  const web = initialSegment === WEBSITE_SLUG;
  const [form, setForm] = useState({ ...EMPTY, segment: initialSegment });
  const [file, setFile] = useState(null);
  const [agree, setAgree] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [error, setError] = useState("");
  const [ticket, setTicket] = useState(null);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const sending = status === "sending";

  function fail(msg) {
    setError(msg);
    setStatus("error");
  }

  function onPickFile(e) {
    const f = e.target.files && e.target.files[0];
    if (f && f.size > ATTACHMENT.maxBytes) {
      e.target.value = "";
      setFile(null);
      fail(`첨부 파일은 ${ATTACHMENT.maxLabel} 까지 올릴 수 있습니다.`);
      return;
    }
    if (status === "error") setStatus("idle");
    setFile(f || null);
  }

  function clearFile() {
    setFile(null);
    if (fileRef.current) fileRef.current.value = "";
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (sending) return;

    if (!form.name.trim()) return fail("이름을 적어주세요.");
    if (!/^[0-9+\-() ]{8,20}$/.test(form.phone.trim())) return fail("연락처를 확인해주세요. 예: 010-1234-5678");
    if (!/^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/.test(form.email.trim())) return fail("이메일 주소를 확인해주세요.");
    if (form.idea.trim().length < 5) return fail(web ? WEBSITE_FORM.ideaError : "아이디어를 한두 줄이라도 적어주세요.");
    if (!agree) return fail("개인정보 수집·이용에 동의하셔야 접수할 수 있습니다.");

    setStatus("sending");
    setError("");
    setProgress(0);

    const a = getAttribution();
    const body = new FormData();
    Object.entries(form).forEach(([k, v]) => body.append(k, v));
    body.append("source", a.source || "");
    body.append("medium", a.medium || "");
    body.append("campaign", a.campaign || "");
    body.append("content", a.content || "");
    body.append("referrer", a.referrer || "");
    body.append("privacy_agree", "true");
    if (file) body.append("attachment", file);

    try {
      // 첨부 업로드 진행률을 보여 주려고 fetch 대신 XHR 을 쓴다 (ui.jsx ContactForm 과 같은 방식)
      const data = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", "/api/landing/inquiry");
        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) setProgress(Math.round((ev.loaded / ev.total) * 100));
        };
        xhr.onload = () => {
          let parsed = {};
          try { parsed = JSON.parse(xhr.responseText || "{}"); } catch { parsed = {}; }
          if (xhr.status >= 200 && xhr.status < 300) resolve(parsed);
          else reject(new Error(typeof parsed.detail === "string" ? parsed.detail : "전송에 실패했습니다. 잠시 후 다시 시도해주세요."));
        };
        xhr.onerror = () => reject(new Error("네트워크 연결을 확인해주세요."));
        xhr.send(body);
      });
      setTicket(data.id || null);
      setForm({ ...EMPTY, segment: form.segment });
      clearFile();
      setAgree(false);
      setStatus("sent");
      window.scrollTo(0, 0);
    } catch (err) {
      setProgress(0);
      fail(err.message || "전송에 실패했습니다.");
    }
  }

  if (status === "sent") {
    return (
      <div className="contact__card lp-done" role="status">
        <h2 className="form__done-title">문의가 접수되었습니다.</h2>
        <p className="form__done-desc">
          {ticket ? `접수번호 L-${ticket}. ` : ""}
          {web ? WEBSITE_FORM.done : "보내 주신 내용을 먼저 검토한 뒤"} 영업일 기준 {REPLY_DAYS}일 이내에 연락드리겠습니다.
          {web ? "" : " 아이디어 내용은 외부에 공개하지 않습니다."}
        </p>
        <p className="form__done-desc">입력하신 이메일로 접수 확인 메일을 보내 드립니다.</p>
        {web ? (
          <Link to={`/for/${WEBSITE_SLUG}`} className="btn btn--ghost btn--block">홈페이지 안내로 돌아가기</Link>
        ) : (
          <Link to="/for" className="btn btn--ghost btn--block">다른 분야 둘러보기</Link>
        )}
      </div>
    );
  }

  return (
    <form className="contact__card form lp-form" onSubmit={onSubmit} noValidate>
      <p className="lp-form__intro">{web ? WEBSITE_FORM.intro : FORM_INTRO}</p>

      <div className="form__row">
        <div className="form__field">
          <label className="form__label" htmlFor="lf-name">이름 <span className="form__req" aria-hidden="true">*</span></label>
          <input id="lf-name" className="form__input" type="text" required maxLength={60}
            autoComplete="name" value={form.name} onChange={update("name")} disabled={sending} />
        </div>
        <div className="form__field">
          <label className="form__label" htmlFor="lf-phone">연락처 <span className="form__req" aria-hidden="true">*</span></label>
          <input id="lf-phone" className="form__input" type="tel" required maxLength={20} inputMode="tel"
            autoComplete="tel" placeholder="010-1234-5678" value={form.phone} onChange={update("phone")} disabled={sending} />
        </div>
      </div>

      <div className="form__field">
        <label className="form__label" htmlFor="lf-email">이메일 <span className="form__req" aria-hidden="true">*</span></label>
        <input id="lf-email" className="form__input" type="email" required maxLength={200}
          autoComplete="email" value={form.email} onChange={update("email")} disabled={sending} />
      </div>

      <div className="form__field">
        <label className="form__label" htmlFor="lf-idea">{web ? WEBSITE_FORM.ideaLabel : "아이디어 요약"} <span className="form__req" aria-hidden="true">*</span></label>
        <textarea id="lf-idea" className="form__input form__textarea" required rows={6} maxLength={5000}
          placeholder={web ? WEBSITE_FORM.ideaPlaceholder : "무엇을 만들고 싶은지, 누가 어디서 쓰는지 편하게 적어 주세요. 한두 줄이어도 괜찮습니다."}
          value={form.idea} onChange={update("idea")} disabled={sending} />
      </div>

      {web ? (
        <WebExtras form={form} update={update} sending={sending}
          file={file} fileRef={fileRef} onPickFile={onPickFile} clearFile={clearFile} />
      ) : (
      <>
      <div className="form__row">
        <Select id="lf-segment" label="분야" value={form.segment} onChange={update("segment")} disabled={sending}
          options={PRODUCT_SEGMENTS.map((s) => [s.slug, s.label])} />
        <Select id="lf-stage" label="현재 단계" value={form.stage} onChange={update("stage")} disabled={sending}
          options={LEAD_STAGES} />
      </div>

      <details className="lp-more">
        <summary className="lp-more__sum">
          더 알려 주시면 검토가 빨라집니다 <span className="form__opt">(모두 선택)</span>
        </summary>
        <div className="lp-more__body">
          <div className="form__row">
            <Select id="lf-option" label="희망 진행 방식" value={form.desired_option} onChange={update("desired_option")}
              disabled={sending} options={LEAD_OPTIONS} />
            <Select id="lf-budget" label="예산 범위" value={form.budget} onChange={update("budget")}
              disabled={sending} options={LEAD_BUDGETS} />
          </div>
          <div className="form__row">
            <div className="form__field">
              <label className="form__label" htmlFor="lf-due">희망 완료 시점</label>
              <input id="lf-due" className="form__input" type="text" maxLength={120} placeholder="예: 내년 3월 전시 전"
                value={form.due} onChange={update("due")} disabled={sending} />
            </div>
            <Select id="lf-qty" label="예상 판매·양산 수량" value={form.quantity} onChange={update("quantity")}
              disabled={sending} options={LEAD_QTYS} />
          </div>
          <div className="form__row">
            <Select id="lf-gov" label="정부지원사업 수행 여부" value={form.gov_support} onChange={update("gov_support")}
              disabled={sending} options={LEAD_GOV} />
            <Select id="lf-video" label="제작 과정 영상 공개" value={form.video_ok} onChange={update("video_ok")}
              disabled={sending} options={LEAD_VIDEO} />
          </div>
          <div className="form__field">
            <label className="form__label" htmlFor="lf-file">참고 파일 · 이미지</label>
            <input ref={fileRef} id="lf-file" className="form__file" type="file"
              accept={ATTACHMENT.accept} onChange={onPickFile} disabled={sending} aria-describedby="lf-file-hint" />
            {file ? (
              <p className="form__file-picked">
                {file.name} <span>{formatSize(file.size)}</span>
                <button type="button" className="form__file-clear" onClick={clearFile} disabled={sending}>제거</button>
              </p>
            ) : (
              <p id="lf-file-hint" className="form__hint">스케치·사진·문서 등, {ATTACHMENT.hint}</p>
            )}
          </div>
        </div>
      </details>
      </>
      )}

      {/* 봇 트랩 — 사람에게는 보이지 않음 */}
      <div className="form__trap" aria-hidden="true">
        <label htmlFor="lf-website">홈페이지</label>
        <input id="lf-website" type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={update("website")} />
      </div>

      <div className="form__agree">
        <input id="lf-privacy" type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} disabled={sending} />
        <label htmlFor="lf-privacy">
          <Link to="/privacy" target="_blank">개인정보 수집·이용</Link>에 동의합니다.
          <span className="form__req" aria-hidden="true"> *</span>
        </label>
      </div>

      {status === "error" && <p className="form__error" role="alert">{error}</p>}

      <button type="submit" className="btn btn--primary btn--block" disabled={sending}>
        {sending ? (file && progress > 0 && progress < 100 ? `첨부 올리는 중… ${progress}%` : "보내는 중…") : web ? WEBSITE_FORM.submit : "상담 문의 보내기"}
        {!sending && <Icon.ArrowRight width="16" height="16" aria-hidden="true" focusable="false" />}
      </button>

      <p className="form__note">
        보내 주신 내용은 상담 회신 목적으로만 사용합니다. 직접 연락을 원하시면{" "}
        <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a> ·{" "}
        <a href={`tel:${COMPANY.phone.replace(/-/g, "")}`}>{COMPANY.phone}</a>
      </p>
    </form>
  );
}

export default function LandingContact() {
  const [params] = useSearchParams();
  const from = params.get("from") || "";
  const remembered = lastSegment();
  const initialSegment = SEGMENT_BY_SLUG[from] ? from : SEGMENT_BY_SLUG[remembered] ? remembered : "";
  const web = initialSegment === WEBSITE_SLUG;

  useSeo(
    web
      ? { title: `${WEBSITE_FORM.title} | I.META`, description: WEBSITE_FORM.lead, path: "/for/contact" }
      : {
          title: "아이디어 상담 문의 | I.META",
          description: "구체적이지 않아도 괜찮습니다. 아이디어 내용은 외부에 공개하지 않습니다.",
          path: "/for/contact",
        }
  );

  useEffect(() => {
    trackVisit("contact");
  }, []);

  return (
    <>
      <section className="lp-hero lp-hero--contact">
        <div className="lp-hero__inner">
          <div className="kicker">Contact</div>
          <h1 className="lp-hero__title">{web ? WEBSITE_FORM.title : "아이디어 상담 문의"}</h1>
          <p className="lp-hero__lead">
            {web
              ? WEBSITE_FORM.lead
              : `아이디어를 남겨 주시면 서면으로 먼저 검토하고, 영업일 기준 ${REPLY_DAYS}일 이내에 연락드립니다.`}
          </p>
        </div>
      </section>

      <section className="section section--tight">
        <div className="lp-contact">
          <LeadForm key={web ? "web" : "product"} initialSegment={initialSegment} />

          {web ? (
            <aside className="lp-contact__aside">
              <div className="contact__panel">
                <h2 className="contact__panel-title">{WEBSITE_FORM.asideTitle}</h2>
                <ol className="lp-mini-steps">
                  {WEBSITE_FORM.flow.map((s) => (
                    <li key={s}><strong>{s}</strong></li>
                  ))}
                </ol>
              </div>
              <div className="contact__panel">
                <h2 className="contact__panel-title">{WEBSITE_FORM.noteTitle}</h2>
                <p className="contact__panel-note">{WEBSITE_FORM.note}</p>
              </div>
            </aside>
          ) : (
          <aside className="lp-contact__aside">
            <div className="contact__panel">
              <h2 className="contact__panel-title">문의 후에는 이렇게 진행됩니다</h2>
              <ol className="lp-mini-steps">
                {LANDING_PROCESS.map((s) => (
                  <li key={s.title}>
                    <strong>{s.title}</strong>
                    <span>{s.desc}</span>
                  </li>
                ))}
              </ol>
            </div>
            <div className="contact__panel">
              <h2 className="contact__panel-title">아이디어 비밀 보장</h2>
              <p className="contact__panel-note">
                보내 주신 아이디어는 검토 목적으로만 쓰고 외부에 공개하지 않습니다. 필요하시면 상담 전에
                비밀유지계약(NDA)을 맺습니다.
              </p>
            </div>
          </aside>
          )}
        </div>
      </section>
    </>
  );
}
